import React, { useContext, useMemo } from "react";
import { Dish } from "@ckal-software/ckal-lib/dist/apps/madplanen";
import { DataContext } from "../../lib/contexts";
import { categoryToIcon } from "../../lib/definitions";
import { categoryToColor } from "../../lib/lib";
import { EcoLabel } from "../elements/ecoLabel";
import { TEXT_COLOR } from "../../lib/style_definitions";

interface IngredientListProps {
  ingredients: Dish["ingredients"];
}

export function IngredientList({ ingredients }: IngredientListProps) {
  const { itemsMap } = useContext(DataContext);

  const items = useMemo(
    () =>
      ingredients
        .map((ing) => ({ ...itemsMap[ing.id], amount: ing.amount }))
        .filter((ing) => ing.name)
        .sort(
          (a, b) =>
            a.category.localeCompare(b.category) * 10 +
            a.name.localeCompare(b.name),
        ),
    [ingredients, itemsMap],
  );

  if (items.length === 0) return null;

  return (
    <div style={container}>
      {items.map((ing, i) => {
        const color = categoryToColor[ing.category as keyof typeof categoryToColor] || "#6b7280";
        const newCategory = i > 0 && items[i - 1].category !== ing.category;
        return (
          <React.Fragment key={ing._id}>
            {newCategory && <div style={categoryDivider} />}
            <div style={itemRow}>
              <div style={itemLeft}>
                <div style={iconWrap(color)}>
                  {categoryToIcon[ing.category as keyof typeof categoryToIcon]}
                </div>
                <span>{ing.name}</span>
                {ing.isOrganic && <EcoLabel size={1} />}
              </div>
              {ing.amount > 1 && <div style={amountBadge}>{ing.amount}</div>}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

const container: React.CSSProperties = {
  background: "#fafbfc",
  borderRadius: 16,
  overflow: "hidden",
  border: "1px solid #e5e7eb",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
  padding: "4px 0",
};

const itemRow: React.CSSProperties = {
  padding: "4px 12px 4px 12px",
  fontSize: 14,
  fontWeight: 500,
  color: TEXT_COLOR,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const categoryDivider: React.CSSProperties = {
  height: 1,
  background: "#e5e7eb",
  margin: "2px 12px",
};

const itemLeft: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  flex: 1,
  minWidth: 0,
};

const iconWrap = (color: string): React.CSSProperties => ({
  width: 28,
  height: 28,
  borderRadius: 8,
  background: `color-mix(in srgb, ${color} 15%, transparent)`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 14,
  flexShrink: 0,
});

const amountBadge: React.CSSProperties = {
  minWidth: 26,
  height: 26,
  borderRadius: 8,
  background: "#eff6ff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 13,
  fontWeight: 600,
  color: "#2563eb",
  border: "1px solid #bfdbfe",
};
